import { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import type { EditingVariable, ThemeValues, ThemeVariable } from "@/domains/theme/types"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { ColorPicker } from "@/domains/theme/components/color-picker"
import { InlineColorPicker } from "@/domains/theme/components/inline-color-picker"
import { ThemeExportModal } from "@/domains/theme/components/theme-export-modal"
import { ThemeImportModal } from "@/domains/theme/components/theme-import-modal"
import { themeVariables } from "@/domains/theme/theme-variables"

import { copyToClipboard } from "../copy-to-clipboard"

export const ThemeVariableSidebar = () => {
  const [editingVariable, setEditingVariable] = useState<EditingVariable | undefined>(undefined)
  const [currentValues, setCurrentValues] = useState<ThemeValues>({})
  const [defaultValues, setDefaultValues] = useState<ThemeValues>({})
  const [editingValues, setEditingValues] = useState<ThemeValues>({})
  const [showExportModal, setShowExportModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [inlineEditing, setInlineEditing] = useState<string | undefined>(undefined)

  // Define all theme variables from globals.css

  const parseHSL = (hslValue: string): { h: number; s: number; l: number } | undefined => {
    const hslMatch = hslValue.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
    if (hslMatch) {
      return {
        h: parseFloat(hslMatch[1] ?? ""),
        s: parseFloat(hslMatch[2] ?? ""),
        l: parseFloat(hslMatch[3] ?? ""),
      }
    }
    return undefined
  }

  const formatHSL = (h: number, s: number, l: number): string => {
    // Format all values to 2 decimal places
    const formattedH = parseFloat(h.toFixed(2))
    const formattedS = parseFloat(s.toFixed(2))
    const formattedL = parseFloat(l.toFixed(2))
    return `${formattedH} ${formattedS}% ${formattedL}%`
  }

  // Format HSL string for display (ensures 2 decimal places)
  const formatHSLString = (hslValue: string): string => {
    const hsl = parseHSL(hslValue)
    if (hsl) {
      return formatHSL(hsl.h, hsl.s, hsl.l)
    }
    return hslValue
  }

  const updateCSSVariable = (variableName: string, newValue: string) => {
    document.documentElement.style.setProperty(`--${variableName}`, newValue)

    // Update the current values state to reflect the change
    const updatedValues: ThemeValues = {
      ...currentValues,
      [variableName]: newValue,
    }

    // Auto-update light and shade variants for p100, p200, p300, p400
    const baseColorPattern = /^(p100|p200|p300|p400)$/
    if (baseColorPattern.test(variableName)) {
      const hsl = parseHSL(newValue)
      if (hsl) {
        const { h, s, l } = hsl

        // Determine offset based on variable name
        // P400: ±4, P300: ±5, P200 and P100: ±3
        const offsetMap: Record<string, number> = {
          p400: 4,
          p300: 5,
          p200: 3,
          p100: 3,
        }
        const offset = offsetMap[variableName] ?? 6

        // Calculate light variant (L + offset%, clamped to 0-100)
        const lightL = Math.min(100, Math.max(0, l + offset))
        const lightValue = formatHSL(h, s, lightL)
        const lightVarName = `${variableName}-light`
        document.documentElement.style.setProperty(`--${lightVarName}`, lightValue)
        updatedValues[lightVarName] = lightValue

        // Calculate shade variant (L - offset%, clamped to 0-100)
        const shadeL = Math.min(100, Math.max(0, l - offset))
        const shadeValue = formatHSL(h, s, shadeL)
        const shadeVarName = `${variableName}-shade`
        document.documentElement.style.setProperty(`--${shadeVarName}`, shadeValue)
        updatedValues[shadeVarName] = shadeValue

        // Clear editing state for auto-updated variants
        setEditingValues((prev) => {
          const updated = { ...prev }
          delete updated[lightVarName]
          delete updated[shadeVarName]
          return updated
        })

        // Exit inline editing mode if this variable is being edited inline
        // This prevents stale input values when slider updates the value
        if (inlineEditing === variableName) {
          setEditingValues((prev) => {
            const updated = { ...prev }
            delete updated[variableName]
            return updated
          })
          setInlineEditing(undefined)
        }
      }
    }

    setCurrentValues(updatedValues)
  }

  const getHSLColor = (hslValue: string) => {
    return `hsl(${hslValue})`
  }

  const handleTextInputChange = (variableName: string, newValue: string) => {
    setEditingValues((prev) => ({
      ...prev,
      [variableName]: newValue,
    }))
  }

  const handleTextInputBlur = (variableName: string) => {
    const newValue = editingValues[variableName]
    if (newValue && newValue !== currentValues[variableName]) {
      updateCSSVariable(variableName, newValue)
      // Clear the editing value after update
      setEditingValues((prev) => {
        const updated = { ...prev }
        delete updated[variableName]
        return updated
      })
    }
  }

  const getCurrentCSSValue = (variableName: string) => {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${variableName}`).trim()
  }

  // LocalStorage functions
  const saveThemeToStorage = () => {
    try {
      localStorage.setItem("theme-builder-values", JSON.stringify(currentValues))
      // alert("Theme saved successfully!")
    } catch (error) {
      console.error("Failed to save theme:", error)
      alert("Failed to save theme. Please try again.")
    }
  }

  const loadThemeFromStorage = () => {
    try {
      const savedValues = localStorage.getItem("theme-builder-values")
      if (savedValues) {
        const parsedValues = JSON.parse(savedValues)
        // Apply saved values to CSS variables
        Object.entries(parsedValues).forEach(([variableName, value]) => {
          document.documentElement.style.setProperty(`--${variableName}`, value as string)
        })
        setCurrentValues(parsedValues)
      } else {
        alert("No saved theme found.")
      }
    } catch (error) {
      console.error("Failed to load theme:", error)
      alert("Failed to load theme. Please try again.")
    }
  }

  const resetToDefaults = () => {
    // Reset CSS variables to default values (but keep saved theme in localStorage)
    Object.entries(defaultValues).forEach(([variableName, value]) => {
      document.documentElement.style.setProperty(`--${variableName}`, value as string)
    })

    setCurrentValues(defaultValues)

    // Reset table mappings to defaults
    const tableDefaultMappings: Record<string, string> = {
      "table-bg": "p300",
      "table-border": "p300",
      "table-head-bg": "p300",
      "table-divider": "p400",
      "table-row-bg": "p400",
      "table-row-alt-bg": "p400",
      "table-row-border": "transparent",
      "table-cell-bg": "transparent",
      "table-cell-hover-bg": "p400-light",
    }

    Object.entries(tableDefaultMappings).forEach(([cssVar, pVar]) => {
      if (pVar === "transparent") {
        document.documentElement.style.setProperty(`--${cssVar}`, "transparent")
      } else {
        const hslValues = getComputedStyle(document.documentElement).getPropertyValue(`--${pVar}`).trim()
        const value = `hsl(${hslValues})`
        document.documentElement.style.setProperty(`--${cssVar}`, value)
      }
    })

    // Clear saved table mappings from localStorage
    localStorage.removeItem("component-table-mappings")
  }

  const handleImportTheme = (importedValues: Record<string, string>) => {
    // Apply imported values to CSS variables
    Object.entries(importedValues).forEach(([variableName, value]) => {
      document.documentElement.style.setProperty(`--${variableName}`, value)
    })

    // Update current values state
    setCurrentValues((prev) => ({
      ...prev,
      ...importedValues,
    }))
  }

  // Initialize current values from CSS variables and store defaults
  useEffect(() => {
    const initialValues: ThemeValues = {}
    const defaults: ThemeValues = {}

    themeVariables.forEach((variable) => {
      const currentValue = getCurrentCSSValue(variable.name)
      if (currentValue) {
        initialValues[variable.name] = currentValue
        defaults[variable.name] = variable.value // Store original default value
      }
    })

    setCurrentValues(initialValues)
    setDefaultValues(defaults)

    // Try to load saved theme on mount
    const savedValues = localStorage.getItem("theme-builder-values")
    if (savedValues) {
      try {
        const parsedValues = JSON.parse(savedValues)
        // Apply saved values to CSS variables
        Object.entries(parsedValues).forEach(([variableName, value]) => {
          document.documentElement.style.setProperty(`--${variableName}`, value as string)
        })
        setCurrentValues(parsedValues)
      } catch (error) {
        console.error("Failed to load saved theme:", error)
      }
    }

    // Load sticky preference
    const savedSticky = localStorage.getItem("theme-builder-sticky")
    if (savedSticky !== null) {
      setIsSticky(savedSticky === "true")
    }
  }, [])

  const handleStickyToggle = () => {
    const newSticky = !isSticky
    setIsSticky(newSticky)
    localStorage.setItem("theme-builder-sticky", newSticky.toString())
  }

  const groupedVariables = themeVariables.reduce(
    (acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = []
      }

      acc[variable.category]!.push(variable)
      return acc
    },
    {} as Record<string, ThemeVariable[]>,
  )

  return (
    <>
      <div
        className={`h-screen w-full max-w-[320px] overflow-y-auto border-l border-secondary-700 bg-secondary-800 p-4 ${
          isSticky ? "sticky top-0 z-50" : ""
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-50">Theme Builder</h2>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isSticky}
                onChange={handleStickyToggle}
                className="h-4 w-4 cursor-pointer rounded border-secondary-700 bg-secondary-800 text-primary-600 focus:ring-primary-500"
                title="Make panel sticky"
              />
              <span className="text-xs text-secondary-300">Sticky</span>
            </label>
            <BodyCaption>
              <Link to="/design" className="text-secondary-300 transition-colors hover:text-secondary-100">
                Design
              </Link>
            </BodyCaption>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex flex-col gap-2">
          <div className="flex flex-row gap-2">
            <button
              onClick={saveThemeToStorage}
              className="flex-1 rounded bg-primary-700 px-3 py-2 text-sm text-secondary-50 transition-colors hover:bg-primary-800"
              title="Save theme to browser storage"
            >
              💾 Save
            </button>
            <button
              onClick={loadThemeFromStorage}
              className="flex-1 rounded bg-primary-700 px-3 py-2 text-sm text-secondary-50 transition-colors hover:bg-primary-800"
              title="Load theme from browser storage"
            >
              📁 Load
            </button>
            <button
              onClick={resetToDefaults}
              className="flex-1 rounded bg-primary-700 px-3 py-2 text-sm text-secondary-50 transition-colors hover:bg-primary-800"
              title="Reset to default values"
            >
              🔄 Reset
            </button>
          </div>

          <div className="flex flex-row gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              className="flex-1 rounded bg-secondary-700 px-3 py-2 text-sm text-secondary-50 transition-colors hover:bg-secondary-800"
              title="Export theme as CSS to share"
            >
              📤 Copy CSS
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex-1 rounded bg-secondary-700 px-3 py-2 text-sm text-secondary-50 transition-colors hover:bg-secondary-800"
              title="Import CSS from clipboard"
            >
              📥 Paste CSS
            </button>
          </div>
        </div>

        {Object.entries(groupedVariables).map(([category, variables]) => (
          <div key={category} className="mb-6">
            <h3 className="mb-3 text-sm font-medium capitalize text-secondary-300">{category} Colors</h3>

            <div className="space-y-2">
              {variables.map((variable) => {
                // Special handling for p100, p200, p300, p400 - group with light and shade
                const pLevelPattern = /^(p100|p200|p300|p400)$/
                if (pLevelPattern.test(variable.name)) {
                  const baseValue = currentValues[variable.name] || variable.value
                  const lightValue =
                    currentValues[`${variable.name}-light`] || getCurrentCSSValue(`${variable.name}-light`)
                  const shadeValue =
                    currentValues[`${variable.name}-shade`] || getCurrentCSSValue(`${variable.name}-shade`)

                  return (
                    <div key={`${variable.name}-group`} className="space-y-2 rounded bg-secondary-800 p-2">
                      {/* light: Full-width color block */}
                      <div
                        className="flex items-center justify-between rounded px-3 py-2"
                        style={{ backgroundColor: getHSLColor(lightValue) }}
                      >
                        <div className="font-mono text-xs text-secondary-50">{variable.name}-light</div>
                        <div className="font-mono text-xs text-secondary-50">{formatHSLString(lightValue)}</div>
                      </div>

                      {/* base: Editable with inline color picker for p100-p400, regular input for others */}
                      {pLevelPattern.test(variable.name) ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="truncate font-mono text-xs text-secondary-50">{variable.name}</div>
                            <div className="flex items-center gap-2">
                              {inlineEditing === variable.name ? (
                                <input
                                  type="text"
                                  value={editingValues[variable.name] ?? baseValue}
                                  onChange={(e) => handleTextInputChange(variable.name, e.target.value)}
                                  onBlur={() => {
                                    handleTextInputBlur(variable.name)
                                    setInlineEditing(undefined)
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleTextInputBlur(variable.name)
                                      setInlineEditing(undefined)
                                      e.currentTarget.blur()
                                    } else if (e.key === "Escape") {
                                      setEditingValues((prev) => {
                                        const updated = { ...prev }
                                        delete updated[variable.name]
                                        return updated
                                      })
                                      setInlineEditing(undefined)
                                      e.currentTarget.blur()
                                    }
                                  }}
                                  autoFocus
                                  className="font-mono text-xs text-secondary-300 bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-0 m-0 w-auto min-w-[120px]"
                                />
                              ) : (
                                <div
                                  className="font-mono text-xs text-secondary-300 cursor-text"
                                  onClick={() => setInlineEditing(variable.name)}
                                >
                                  {formatHSLString(baseValue)}
                                </div>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  copyToClipboard(formatHSLString(baseValue))
                                }}
                                className="text-secondary-400 transition-colors hover:text-secondary-300"
                                title="Copy HSL values"
                              >
                                📋
                              </button>
                            </div>
                          </div>
                          <InlineColorPicker
                            variableName={variable.name}
                            currentValue={baseValue}
                            onUpdate={updateCSSVariable}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          {/* Color swatch */}
                          <div
                            className="h-8 w-8 flex-shrink-0 rounded border border-secondary-700"
                            style={{ backgroundColor: getHSLColor(baseValue) }}
                          />

                          {/* Variable info */}
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-mono text-xs text-secondary-50">{variable.name}</div>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editingValues[variable.name] ?? baseValue}
                                onChange={(e) => handleTextInputChange(variable.name, e.target.value)}
                                onBlur={() => handleTextInputBlur(variable.name)}
                                className="flex-1 rounded border border-secondary-700 bg-secondary-800 px-2 py-1 font-mono text-xs text-secondary-300 outline-none focus:border-secondary-700 focus:bg-secondary-800"
                                placeholder="HSL values"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  copyToClipboard(formatHSLString(baseValue))
                                }}
                                className="text-secondary-400 transition-colors hover:text-secondary-300"
                                title="Copy HSL values"
                              >
                                📋
                              </button>
                            </div>
                          </div>

                          {/* Edit button */}
                          <button
                            onClick={() => setEditingVariable({ ...variable, value: baseValue })}
                            className="rounded bg-secondary-800 px-2 py-1 text-xs text-secondary-400 transition-colors hover:bg-secondary-700 hover:text-secondary-50"
                          >
                            Edit
                          </button>
                        </div>
                      )}

                      {/* shade: Full-width color block */}
                      <div
                        className="flex items-center justify-between rounded px-3 py-2"
                        style={{ backgroundColor: getHSLColor(shadeValue) }}
                      >
                        <div className="font-mono text-xs text-secondary-50">{variable.name}-shade</div>
                        <div className="font-mono text-xs text-secondary-50">{formatHSLString(shadeValue)}</div>
                      </div>
                    </div>
                  )
                }

                // Skip p100-light, p100-shade, p200-light, p200-shade, p300-light, p300-shade, p400-light, p400-shade as they're handled in their groups
                if (variable.name.match(/^(p100|p200|p300|p400)-(light|shade)$/)) {
                  return null
                }

                // Regular variable rendering for all others
                const currentValue = currentValues[variable.name] || variable.value
                return (
                  <div key={variable.name} className="flex items-center gap-3 rounded bg-secondary-800 p-2">
                    {/* Color swatch */}
                    <div
                      className="h-8 w-8 flex-shrink-0 rounded border border-secondary-700"
                      style={{ backgroundColor: getHSLColor(currentValue) }}
                    />

                    {/* Variable info */}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-xs text-secondary-50">{variable.name}</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingValues[variable.name] ?? currentValue}
                          onChange={(e) => handleTextInputChange(variable.name, e.target.value)}
                          onBlur={() => handleTextInputBlur(variable.name)}
                          className="flex-1 rounded border border-secondary-700 bg-secondary-800 px-2 py-1 font-mono text-xs text-secondary-300 outline-none focus:border-secondary-700 focus:bg-secondary-800"
                          placeholder="HSL values"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            copyToClipboard(formatHSLString(currentValue))
                          }}
                          className="text-secondary-400 transition-colors hover:text-secondary-300"
                          title="Copy HSL values"
                        >
                          📋
                        </button>
                      </div>
                    </div>

                    {/* Edit button */}
                    <button
                      onClick={() => setEditingVariable({ ...variable, value: currentValue })}
                      className="rounded bg-secondary-800 px-2 py-1 text-xs text-secondary-400 transition-colors hover:bg-secondary-700 hover:text-secondary-50"
                    >
                      Edit
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Color Picker Modal */}
      {editingVariable && (
        <ColorPicker
          variable={editingVariable}
          onUpdate={updateCSSVariable}
          onClose={() => setEditingVariable(undefined)}
        />
      )}

      {/* Export Modal */}
      {showExportModal && <ThemeExportModal currentValues={currentValues} onClose={() => setShowExportModal(false)} />}

      {/* Import Modal */}
      {showImportModal && <ThemeImportModal onImport={handleImportTheme} onClose={() => setShowImportModal(false)} />}
    </>
  )
}
